import { useData } from "./useData";
import { useCRUD } from "./useCRUD";
import { PropertyRepository } from "@/repositories/PropertyRepository";
import type { Property, PropertyInsert, PropertyUpdate } from "@/types";

const propertyRepository = new PropertyRepository();

export function useProperties(filters?: {
  search?: string;
  status?: "active" | "hidden" | "inactive" | "draft";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
  owner_id?: string;
  showDeleted?: boolean;
}) {
  const { data, isLoading, error, refetch } = useData<Property>({
    repository: propertyRepository,
    fetcher: (repo) => repo.getAll(filters),
    queryKey: ["properties", JSON.stringify(filters)],
  });

  const {
    create,
    update,
    delete: softDelete,
    restore,
    isCreating,
    isUpdating,
    isDeleting,
    isRestoring,
  } = useCRUD<Property, PropertyInsert, PropertyUpdate>({
    repository: propertyRepository,
    queryKey: ["properties", JSON.stringify(filters)],
    messages: {
      createSuccess: "Propriedade criada com sucesso.",
      updateSuccess: "Propriedade atualizada com sucesso.",
      deleteSuccess: "Propriedade excluída com sucesso.",
      restoreSuccess: "Propriedade restaurada com sucesso.",
    },
  });

  // Type assertion to help TypeScript understand the paginated response structure
  const paginatedData = data as { items: Property[]; count: number } | null;

  return {
    properties: paginatedData?.items ?? [],
    count: paginatedData?.count ?? 0,
    isLoading,
    error,
    refetch,
    createProperty: create,
    updateProperty: update,
    softDelete,
    restore,
    isCreating,
    isUpdating,
    isDeleting,
    isRestoring,
  };
}
